import PropTypes from 'prop-types';
import { Link } from 'react-router';

import { fetchCustomerDetail } from '#/api/crmMgmt/customer/fetchDetail';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import SideMsg from '#/modules/commonComponents/SideMsg';
import {
  customerSideMsgDetailBlock,
  customerSideMsgStatusBlock,
} from '#/modules/crmMgmt/customer/data/customerSideMsgConfig';
import {
  canActivateCustomer,
  canDeactivateCustomer,
} from '#/modules/crmMgmt/customer/helper/customerStatus';
import useCustomerStatusMutation, {
  CUSTOMER_ACTIVATE_CONFIRM,
  CUSTOMER_DEACTIVATE_CONFIRM,
} from '#/modules/crmMgmt/customer/hooks/useCustomerStatusMutation';
import { useCan } from '#/modules/infra/auth/useCan';

const CustomerSideMsg = ({ displayData, setSelectedData }) => {
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const canUpdate = useCan(PERM.CUSTOMER_UPDATE);
  const canActivatePerm = useCan(PERM.CUSTOMER_ACTIVATE);

  const id = displayData?.id;
  const status = displayData?.status;
  const updatedAt = displayData?.record?.updatedAt ?? displayData?.updatedAt;
  const canActivate = canActivatePerm && canActivateCustomer(status);
  const canDeactivate = canActivatePerm && canDeactivateCustomer(status);

  const statusMutation = useCustomerStatusMutation({
    id,
    getUpdatedAt: () => updatedAt,
    onAfterSuccess: async () => {
      if (!id) {
        return;
      }
      try {
        const detail = await fetchCustomerDetail(id);
        setSelectedData(detail?.data ?? null);
      } catch {
        setSelectedData(null);
      }
    },
    onVersionConflict: () => {
      if (!id) {
        return;
      }
      fetchCustomerDetail(id)
        .then((detail) => setSelectedData(detail?.data ?? null))
        .catch(() => setSelectedData(null));
    },
  });

  const isPending = statusMutation.isPending;
  const canOpenDetail = Boolean(id);
  const canEdit = canUpdate && Boolean(id);
  const hasActions = canOpenDetail || canEdit || canActivate || canDeactivate;

  async function handleActivate() {
    const ok = await confirm(CUSTOMER_ACTIVATE_CONFIRM);
    if (!ok) {
      return;
    }
    statusMutation.mutate({ action: 'activate' });
  }

  async function handleDeactivate() {
    const ok = await confirm(CUSTOMER_DEACTIVATE_CONFIRM);
    if (!ok) {
      return;
    }
    statusMutation.mutate({ action: 'deactivate' });
  }

  const blocks = [
    customerSideMsgStatusBlock,
    {
      condition: () => hasActions,
      fields: [
        {
          key: 'actions',
          label: '操作',
          render: () => (
            <div className="side_msg-row">
              <h3>操作</h3>
              <div className="flex flex-wrap items-center gap-2">
                {canOpenDetail ? (
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.customerDetail(id)}>詳情</Link>
                  </Button>
                ) : null}
                {canEdit ? (
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.customerEdit(id)}>編輯</Link>
                  </Button>
                ) : null}
                {canActivate ? (
                  <Button type="button" disabled={isPending} onClick={handleActivate}>
                    啟用
                  </Button>
                ) : null}
                {canDeactivate ? (
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={isPending}
                    onClick={handleDeactivate}
                  >
                    停用
                  </Button>
                ) : null}
              </div>
            </div>
          ),
        },
      ],
    },
    customerSideMsgDetailBlock,
  ];

  return (
    <>
      {ConfirmDialogHost}
      <SideMsg
        title="客戶"
        titleValue="name"
        displayData={displayData}
        blocks={blocks}
      />
    </>
  );
};

CustomerSideMsg.propTypes = {
  displayData: PropTypes.object,
  setSelectedData: PropTypes.func.isRequired,
};

export default CustomerSideMsg;
